import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarClock, MessagesSquare } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SidePanel } from '../../../components/design-system/common/SidePanel';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { ArdiActivityItem, ArdiStatus, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { useNotificationStore } from '../../../common/stores/notification.store';
import { ArdiActivityFeed } from './ArdiActivityFeed';

const REFRESH_MS = 60_000;

/** Ardi's status, kept fresh: every minute, and whenever a notification arrives. */
export function useArdiStatus() {
  const api = useArdiApi();
  const [status, setStatus] = useState<ArdiStatus | null>(null);
  const lastArrival = useNotificationStore((s) => s.lastArrival);

  const load = useCallback(() => {
    api.getStatus().then(setStatus).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (lastArrival) load();
  }, [lastArrival, load]);

  return { status, reload: load };
}

/** How long ago, in a few words: "just now", "4 min ago". */
function sinceWords(t: (k: string, o?: any) => string, iso: string | null, now = Date.now()) {
  if (!iso) return null;
  const minutes = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return t('ARDI.STATUS.JUST_NOW');
  if (minutes < 60) return t('ARDI.STATUS.MINUTES_AGO', { count: minutes });
  return t('ARDI.STATUS.HOURS_AGO', { count: Math.round(minutes / 60) });
}

/**
 * The line that says Ardi is working when nobody is asking it anything.
 *
 * A pulsing dot and what it has actually done today — checks run, shopper
 * actions read, things noticed — so "24/7" is something a vendor can see,
 * with the log one click away. Honest about the rest: when the watch is off
 * (a developer machine) or has not run yet, it says that instead.
 */
export function ArdiStatusPill({ status, onOpen }: { status: ArdiStatus | null; onOpen: () => void }) {
  const { t } = useTranslation();
  if (!status) return <Skeleton className="h-7 w-72 rounded-full" />;

  const since = sinceWords(t as any, status.lastPulseAt);
  const live = status.on && !!status.lastPulseAt;
  const parts: string[] = [];
  if (!status.on) parts.push(t('ARDI.STATUS.OFF'));
  else if (!status.lastPulseAt) parts.push(t('ARDI.STATUS.STARTING'));
  else {
    parts.push(t('ARDI.STATUS.CHECKED', { when: since }));
    if (status.eventsToday) parts.push(t('ARDI.STATUS.READ_TODAY', { count: status.eventsToday }));
    parts.push(status.noticed24h ? t('ARDI.STATUS.NOTICED', { count: status.noticed24h }) : t('ARDI.STATUS.ALL_QUIET'));
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex max-w-full items-center gap-2 rounded-full border bg-background py-1 ps-2.5 pe-3 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
      data-testid="ardi-status"
    >
      <span className="relative flex size-2 shrink-0" aria-hidden>
        {live && <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />}
        <span className={cn('relative inline-flex size-2 rounded-full', live ? (status.silent ? 'bg-warning' : 'bg-success') : 'bg-muted-foreground/40')} />
      </span>
      <span className="font-medium text-foreground">{t('ARDI.STATUS.NAME')}</span>
      <span className="truncate">{parts.join(' · ')}</span>
    </button>
  );
}

/** Everything Ardi did on its own, with what it runs on: routines and channels. */
export function ArdiLogPanel({ open, onOpenChange, status }: { open: boolean; onOpenChange: (o: boolean) => void; status: ArdiStatus | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useArdiApi();
  const [items, setItems] = useState<ArdiActivityItem[] | null>(null);
  const [more, setMore] = useState(true);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.getActivity({ limit: 30 }).then((r) => {
      setItems(r);
      setMore(r.length === 30);
    }).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status?.lastPulseAt]);

  const loadMore = async () => {
    const last = items?.[items.length - 1];
    if (!last) return;
    const next = await api.getActivity({ limit: 30, before: last.createdAt }).catch(() => []);
    setItems((prev) => [...(prev ?? []), ...next]);
    setMore(next.length === 30);
  };

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange} title={t('ARDI.LOG.TITLE') as string} description={t('ARDI.LOG.DESC') as string} width="data-[side=right]:sm:max-w-[520px]">
      <div className="flex flex-col gap-6">
        {status && (
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('ARDI.LOG.CHECKS')} value={status.checksToday} />
            <Stat label={t('ARDI.LOG.READ')} value={status.eventsToday} />
            <Stat label={t('ARDI.LOG.NOTICED')} value={status.noticed24h} />
          </div>
        )}
        {status && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            {status.lastPulseAt ? (
              <>
                {t('ARDI.LOG.LAST_CHECK')} <RelativeTime value={status.lastPulseAt} />. {t('ARDI.LOG.HOW')}
              </>
            ) : (
              t(status.on ? 'ARDI.STATUS.STARTING' : 'ARDI.STATUS.OFF')
            )}
          </p>
        )}

        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" onClick={() => go('/home/routines')} className="flex items-center gap-3 rounded-xl border p-3 text-start hover:bg-accent">
            <CalendarClock className="size-4 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-sm">{t('ARDI.LOG.ROUTINES', { count: status?.routines.active ?? 0 })}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {status?.routines.next ? <>{status.routines.next.title} · <RelativeTime value={status.routines.next.at} /></> : t('ARDI.LOG.ROUTINES_NONE')}
              </span>
            </span>
            <ArrowRight className="size-4 text-muted-foreground rtl:rotate-180" aria-hidden />
          </button>
          <button type="button" onClick={() => go('/home/channels')} className="flex items-center gap-3 rounded-xl border p-3 text-start hover:bg-accent">
            <MessagesSquare className="size-4 text-muted-foreground" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block text-sm">{t('ARDI.LOG.CHANNELS', { count: status?.channels.connected ?? 0 })}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {status?.channels.connected ? t('ARDI.LOG.CHATS', { count: status.channels.chats }) : t('ARDI.LOG.CHANNELS_NONE')}
              </span>
            </span>
            <ArrowRight className="size-4 text-muted-foreground rtl:rotate-180" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-medium">{t('ARDI.LOG.RECENT')}</h3>
          {!items ? (
            <div className="flex flex-col gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
          ) : (
            <ArdiActivityFeed items={items} />
          )}
          {items && more && items.length > 0 && (
            <Button variant="ghost" size="sm" onClick={loadMore} className="self-center text-muted-foreground">{t('ARDI.NOTIFICATIONS.LOAD_MORE')}</Button>
          )}
        </div>
      </div>
    </SidePanel>
  );
}

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-xl border p-3">
    <p className="text-lg font-medium tracking-tight tabular-nums">{new Intl.NumberFormat().format(value ?? 0)}</p>
    <p className="text-[11px] leading-tight text-muted-foreground">{label}</p>
  </div>
);
